/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import IconButton from 'apollo-react/components/IconButton';
import MenuItem from 'apollo-react/components/MenuItem';
import Select from 'apollo-react/components/Select';
import Table from 'apollo-react/components/Table';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import ChevronDown from 'apollo-react-icons/ChevronDown';
import ChevronRight from 'apollo-react-icons/ChevronRight';
import Pencil from 'apollo-react-icons/Pencil';
import PlusIcon from 'apollo-react-icons/Plus';
import Trash from 'apollo-react-icons/Trash';
import classNames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import ActionsModal from './ActionsModal';
import { useFormSelector, useTableLastRowVisibilityEffect } from '../../../../../custom-hooks';
import { getUITA } from '../../../../../utils/commonUtils';
import { ALL_SUPPORTED_UPDATE_ACTIONS_TYPES } from '../../../../constants/editor.constants';
import { getEntityDefinition, getTranslations } from '../../../../selectors/common.selectors';
import {
  getDeltaChangesByControl,
  getOriginalPropByModel,
} from '../../../../selectors/editor.selectors';
import {
  colorBorderHandler,
  getActionsText,
  getConditionsText,
  getTranslatedEntityDefinitionOptions,
  tooltipExtraLabelBuilder,
} from '../../../../utils/editor.utils';
import { equalityFn } from '../../../../utils/form-manager.utils';
import ConditionModal from '../ConditionModal/ConditionModal';

const useStyles = makeStyles({
  root: {
    margin: '0 13px 0 13px',
    maxHeight: '800px',
    '& [class*="InternalTableBody-expandedRowEditMode"]': {
      backgroundColor: 'white',
      border: '1px solid #eee',
    },
    '& [class*="MuiTable-root"]': {
      border: 'solid 1px #e9e9e9',
      boxShadow: 'none',
    },
    '& [class*="MuiTableRow-root"]': {
      verticalAlign: 'middle',
    },
    '& [class*="MuiToolbar"]': {
      paddingTop: 0,
      height: 'unset',
    },
    '& > div > div:nth-child(2) > table > thead': {
      display: 'none',
    },
  },

  expandableComponentContainer: {
    display: 'block !important',
    backgroundColor: 'white',
    paddingBottom: '8px',
    marginTop: '5px',
  },

  customTooltip: {
    maxWidth: 'none',
  },

  headerText: {
    padding: '15px 0',
  },

  dataRulesContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },

  containerComponent: {
    margin: 15,

    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: '15px',
    },
  },

  actionsCntainerComponent: {
    margin: '15px 0',

    '& .MuiTableCell-root:first-of-type': {
      paddingLeft: '15px',
    },
  },

  dataMemberName: {
    color: 'white',
    fontWeight: 500,
  },

  titleHeader: {
    color: '#000000',
    fontSize: 18,
    marginBottom: 0,
  },

  customNameContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '15px 0',

    '& .Mui-disabled': {
      opacity: '1',
    },
  },

  customNameActionContainer: {
    display: 'flex',
    alignItems: 'center',

    '& [class*="MuiFormControl-root"]': {
      margin: 0,
    },
  },

  customNameText: {
    width: '100%',
  },

  customNameInput: {
    '& [class*=MuiInput-root]': {
      margin: 0,
    },
  },

  actionCellContainer: {
    display: 'flex',
  },

  currentSection: {
    border: '1px solid blue',
  },

  selected: {
    '& a,  svg, button': {
      color: 'white',
    },
    '& td': {
      backgroundColor: '#0768fd !important',
    },
  },
  sectionName: {
    '& div': {
      paddingRight: '10px',
    },
  },
});

const ExpandCell = ({ row: { id, handleToggleRow, expanded }, column: { accessor: key } }) => {
  const handleClick = (id, key) => {
    handleToggleRow(id, key);
  };

  return (
    <span>
      <IconButton
        id="expand"
        size="small"
        onClick={() => handleClick(id, key)}
        ui-ta={getUITA('Update Rules Table', 'Expand Btn')}
        aria-label={expanded ? 'Collpase' : 'Expand'}
      >
        {expanded ? <ChevronDown /> : <ChevronRight />}
      </IconButton>
    </span>
  );
};

const EditableCellCustomName = ({ row, column: { accessor: key } }) => {
  const { editRow, editedRow, dataMember, entityName, id, classes, updateLocalState } = row;
  const [editMode, setEditMode] = useState(true);
  const [value, setValue] = useState(row[key]);

  const dataMemberName = dataMember.find((item) => item.value === entityName);

  useEffect(() => {
    setValue(row[key]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row[key]]);

  return (
    <div className={classes.customNameContainer}>
      {dataMemberName && (
        <Typography
          style={{ color: editedRow && editedRow.id === row.id ? '#fff' : '#444' }}
          className={classes.dataMemberName}
        >
          {dataMemberName.label}
        </Typography>
      )}
      <div className={classes.customNameActionContainer}>
        {editMode ? (
          <>
            <Typography
              style={{ color: editedRow && editedRow.id === row.id ? '#fff' : '#444' }}
              ui-ta={getUITA('Rule Name')}
              className={classes.customNameText}
            >
              {value || 'Enter rule name'}
            </Typography>
            <div
              ui-ta={getUITA('Edit Rule Name Button')}
              onClick={() => setEditMode((prev) => !prev)}
            >
              <IconButton style={{ marginLeft: 15 }} aria-label="Edit">
                <Pencil size="small" />
              </IconButton>
            </div>
          </>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Enter rule name"
              ui-ta={getUITA('Rule Name')}
              fullWidth
              style={{ width: '80%' }}
              className={classes.customNameInput}
              value={value}
              onChange={({ target }) => setValue(target.value)}
              onBlur={() => editRow(id, key, value)}
            />
            <div
              ui-ta={getUITA('Edit Rule Name Button')}
              onClick={() => setEditMode((prev) => !prev)}
            >
              <Button
                ui-ta={getUITA('Save Rule Name Button')}
                variant="primary"
                size="small"
                onClick={() => updateLocalState()}
                style={{ margin: 8 }}
              >
                {'save'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const EditConditionComponent = ({ row }) => {
  const { Conditions, classes, editedRow } = row;

  const conditionsText = getConditionsText({ conditions: Conditions, isTranslated: true });

  return (
    <Typography
      className={classes.headerText}
      style={{ color: editedRow.id === row.id ? '#fff' : '#444' }}
    >
      {conditionsText}
    </Typography>
  );
};

const EditActionsComponent = ({ row }) => {
  const { actions, classes, editedRow } = row;

  const actionsText = getActionsText({ actions, isTranslated: true }).reduce(
    (acc, action) => `${acc} ${action}`,
    ''
  );

  return (
    <Typography
      className={classes.headerText}
      style={{
        color: editedRow.id === row.id ? '#fff' : '#444',
        whiteSpace: 'break-spaces',
        lineHeight: 2,
      }}
    >
      {actionsText}
    </Typography>
  );
};

const ActionCell = ({ row }) => {
  const { id, onDelete, classes } = row;

  return (
    <div className={classes.actionCellContainer}>
      <div>
        <IconButton onClick={(e) => onDelete(id, e)} aria-label="Delete">
          <Trash />
        </IconButton>
      </div>
    </div>
  );
};

const ExpandableComponent = ({ row }) => {
  const {
    editedRow,
    controlId,
    dataMember,
    entityName,
    Conditions,
    classes,
    onDeleteConditions,
    id,
    currentControlModel,
    setCurrentControlModel,
    updateLocalState,
    editRow,
  } = row;

  const [showConditions, setShowConditions] = useState(false);

  const handleSelect = ({ target }) => {
    const rows = editRow(id, 'entityName', target.value);
    updateLocalState(rows);
  };

  return (
    <div className={classes.expandableComponentContainer}>
      <div style={{ border: '1px solid #eee' }} ui-ta={getUITA('Conditions')}>
        <div className={classes.containerComponent}>
          <h3 className={classes.titleHeader}>{'Conditions'}</h3>
          <div className={classes.dataRulesContainer}>
            <div style={{ marginRight: '10px', flexGrow: 4 }} ui-ta={getUITA('Data Member')}>
              <Select
                label="Data Member"
                size="large"
                fullWidth
                canDeselect={false}
                value={entityName}
                onChange={handleSelect}
              >
                {dataMember.map(({ value, label }) => (
                  <MenuItem key={value} value={value}>
                    {`${label} (${value})`}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div style={{ marginRight: '45px', flexGrow: 8 }}>
              <Tooltip
                title="Conditions"
                extraLabels={tooltipExtraLabelBuilder(Conditions)}
                placement="bottom"
                classes={{
                  tooltip: classes.customTooltip,
                }}
                variant="light"
              >
                <TextField
                  label="Conditions"
                  ui-ta={getUITA('Condition value')}
                  value={
                    (Conditions &&
                      getConditionsText({ conditions: Conditions, showFullText: true })) ||
                    ''
                  }
                  size="large"
                  fullWidth
                  icon={
                    showConditions ? (
                      <ChevronDown
                        fontSize="extraSmall"
                        onClick={() => setShowConditions((prev) => !prev)}
                      />
                    ) : (
                      <ChevronRight
                        ui-ta={getUITA('Expand button')}
                        fontSize="extraSmall"
                        onClick={() => setShowConditions((prev) => !prev)}
                      />
                    )
                  }
                />
              </Tooltip>
            </div>
            <div style={{ marginRight: '10px', flexGrow: 2, marginTop: '40px' }}>
              <IconButton
                ui-ta={getUITA('Delete button')}
                onClick={(e) => {
                  onDeleteConditions(id, e);
                  setShowConditions(false);
                }}
              >
                <Trash />
              </IconButton>
            </div>
          </div>
        </div>
      </div>
      {showConditions && (
        <div style={{ border: '1px solid #eee' }} ui-ta={getUITA('Condition Modal')}>
          <div className={classes.containerComponent}>
            <div>
              <ConditionModal
                name="update"
                ruleId={row.ruleId}
                currentControlModel={currentControlModel}
                setCurrentControlModel={setCurrentControlModel}
              />
            </div>
          </div>
        </div>
      )}
      <div className={classes.actionsCntainerComponent}>
        <div ui-ta={getUITA('Actions Modal')}>
          {editedRow && editedRow.id && (
            <ActionsModal
              controlId={controlId}
              name="update"
              ruleId={editedRow.id}
              currentControlModel={currentControlModel}
              setCurrentControlModel={setCurrentControlModel}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const columnsViewModel = [
  {
    header: '',
    accessor: 'toogleComponent',
    width: '5%',
    customCell: ExpandCell,
  },
  {
    header: '',
    accessor: 'name',
    width: '24%',
    customCell: EditableCellCustomName,
  },
  {
    header: '',
    accessor: 'editConditions',
    width: '32%',
    customCell: EditConditionComponent,
  },
  {
    header: '',
    accessor: 'editActions',
    width: '32%',
    customCell: EditActionsComponent,
  },
  {
    accessor: 'action',
    width: '3%',
    customCell: ActionCell,
  },
];

const RuleModal = ({ controlId, name, currentControlModel, setCurrentControlModel }) => {
  const classes = useStyles();

  const [expandedRow, setExpandedRow] = useState([]);
  const [editedRow, setEditedRow] = useState({});

  const tableRef = useRef(null);

  const propValue = currentControlModel[name];
  const fieldName = currentControlModel.fieldName;

  const { rules = [] } = propValue && propValue.rules ? propValue : {};

  const originalPropValue = useFormSelector(getOriginalPropByModel, { id: controlId, prop: name });

  const entityDefinition = useFormSelector(getEntityDefinition);

  const translations = useFormSelector(getTranslations);
  const { Entity: entityTranslations } = translations;
  const newOptions = useMemo(
    () => getTranslatedEntityDefinitionOptions({ entityDefinition, entityTranslations }),
    [entityDefinition, entityTranslations]
  );

  const previousStateRows = useRef([]);

  const formChangesUpdate = useFormSelector(
    getDeltaChangesByControl,
    {
      id: controlId,
      name: 'update',
    },
    equalityFn
  );
  const { currentValue } =
    (formChangesUpdate && formChangesUpdate[0]?.Properties.find((item) => item.name === name)) ||
    {};

  const options = useRef(newOptions);

  const processedRules = rules.filter(
    (rule) =>
      !rule.actions.length ||
      rule.actions.some(
        (action) => ALL_SUPPORTED_UPDATE_ACTIONS_TYPES.includes(action.type) || !action.type
      )
  );

  const [rows, setRows] = useState(processedRules.length ? processedRules : []);

  useEffect(() => {
    const { rules = [] } = propValue && propValue.rules ? propValue : {};
    const processedRules = rules.filter(
      (rule) =>
        !rule.actions.length ||
        rule.actions.some(
          (action) => ALL_SUPPORTED_UPDATE_ACTIONS_TYPES.includes(action.type) || !action.type
        )
    );
    setRows(processedRules.length ? processedRules : []);
  }, [propValue]);

  useEffect(() => {
    if (rows && rows.length >= 1) {
      setEditedRow(rows[0]);
      const { rules } = originalPropValue ? originalPropValue : [];
      previousStateRows.current = rules ? [...rules] : [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateLocalState = (newRowsTemp) => {
    const newRows = newRowsTemp || rows;

    if (controlId && name) {
      const newValue = newRows.map((item) => ({
        ...item,
        fieldName,
        translatedEntityName: fieldName,
      }));

      setRows(newValue);

      setCurrentControlModel((prev) => ({
        ...prev,
        [name]: {
          rules: newValue && newValue.length ? newValue : [],
        },
      }));
    }
  };

  const handleToggleRow = (Id, key) => {
    setExpandedRow((expandedRow) => (expandedRow.includes(Id) ? [] : [Id, key]));
  };

  const onRowEdit = (id) => {
    setEditedRow(rows.find((row) => row.id === id));
  };

  const onDelete = (id, event) => {
    setEditedRow({});
    const deletedRow = rows.find((row) => row.id === id);

    const unsupportedInteractionActions =
      deletedRow?.actions.filter(
        (action) => !ALL_SUPPORTED_UPDATE_ACTIONS_TYPES.includes(action.type)
      ) || [];

    const newRows = unsupportedInteractionActions.length
      ? rows.map((row) => {
          if (row.id !== id) {
            return row;
          }

          return {
            ...row,
            actions: unsupportedInteractionActions,
          };
        })
      : rows.filter((row) => row.id !== id);

    updateLocalState(newRows);
    event.stopPropagation();
  };

  const onDeleteConditions = (id, event) => {
    const newRows = rows.map((row) =>
      row.id === id ? { ...row, Conditions: [], description: 'True' } : row
    );
    updateLocalState(newRows);
    event.stopPropagation();
  };

  const editRow = (id, key, value) => {
    const updatedRows = rows.map((row) => (row.id === id ? { ...row, [key]: value } : row));
    setRows(updatedRows);

    return updatedRows;
  };

  const AddRow = () => {
    const rowId = `${name}|${controlId}|${uuidv4()}`;
    const newRule = {
      id: rowId,
      rowIndex: rows.length,
      entityName: options.current?.length ? options.current[0].value : '',
      description: 'True',
      Conditions: [],
      actions: [],
      name: '',
    };
    const newRows = [...rows, newRule];
    updateLocalState(newRows);
    setEditedRow(newRows.find((row) => row.id === rowId));
    setExpandedRow([rowId]);
  };

  const CustomHeader = () => (
    <div style={{ marginRight: -14 }}>
      <Button icon={PlusIcon} variant="primary" onMouseUp={AddRow}>
        {'Add Rule'}
      </Button>
    </div>
  );

  useTableLastRowVisibilityEffect({ tableRef, rows, isExpandableRowsTable: true });

  return (
    <div ref={tableRef} className={classes.root} ui-ta={getUITA('Update Rules Table')}>
      <Table
        columns={columnsViewModel}
        title=""
        stripedRows
        isSticky
        CustomHeader={CustomHeader}
        rows={rows.map((row) => ({
          ...row,
          onRowEdit,
          onDelete,
          editRow,
          onDeleteConditions,
          tableRef,
          key: row.id,
          expanded: expandedRow.includes(row.id),
          editMode: editedRow.id === row.id,
          editedRow,
          handleToggleRow,
          controlId,
          propValue,
          currentControlModel,
          setCurrentControlModel,
          dataMember: options.current,
          onClick: () => onRowEdit(row.id),
          classes,
          updateLocalState,
          className: classNames(editedRow.id === row.id ? classes.selected : ''),
          style: {
            borderLeft: colorBorderHandler(
              currentValue && currentValue.rules.some((item) => item.id === row.id),
              !previousStateRows.current.find(
                (item) => row.description === item.description && row.id === item.id
              )
            ),
          },
          ruleId: row.id,
        }))}
        hidePagination
        initialSortOrder="asc"
        ExpandableComponent={ExpandableComponent}
      />
    </div>
  );
};

export default React.memo(RuleModal);
